// Design tokens: the single source of the visual system for the learner app (children
// aged 9 to 14), parent mode and the alpha site. `tokens.css` is generated from this
// file (`pnpm --filter @tutor/ui tokens`) and checked for drift and for WCAG 2.2 AA
// contrast in CI (test/tokens.test.ts). Change a value here, never in the CSS.
//
// Colour names are semantic (what the colour is for), not descriptive. The learner
// app's original variable names (--bg, --surface, --ink, --accent...) are kept so screens
// written before this package keep working.

/** A colour theme: every semantic colour, as `#rrggbb`. */
export interface ColorTheme {
  /** Page background. */
  readonly bg: string;
  /** Cards, sheets, inputs. */
  readonly surface: string;
  /** Inset areas on a surface: wells, chips, the tutor's bubble in dark mode. */
  readonly "surface-2": string;
  /** Body text. */
  readonly ink: string;
  /** Secondary text: hints, captions, timestamps. */
  readonly "ink-soft": string;
  /** Decorative dividers. Never the only thing that shows a control's edge. */
  readonly line: string;
  /** Edges of inputs and other controls (WCAG 1.4.11: 3:1 against their surface). */
  readonly "line-strong": string;
  /** Primary actions and the current selection. */
  readonly accent: string;
  /** Text on `accent`. */
  readonly "accent-ink": string;
  /** Selected backgrounds, the learner's chat bubble. */
  readonly "accent-soft": string;
  /** Text and icons in an `accent-soft` area. */
  readonly "accent-strong": string;
  /** Warm highlight for streaks and celebrations (decorative, never text). */
  readonly sun: string;
  /** Right answers and progress. */
  readonly good: string;
  readonly "good-soft": string;
  /** "Not yet": wrong answers are gentle, never alarming red. */
  readonly gentle: string;
  readonly "gentle-soft": string;
  /** Neutral information: tips, the tutor thinking. */
  readonly info: string;
  readonly "info-soft": string;
  /** Errors a parent must act on (failed payment, expired code). Not for children's answers. */
  readonly danger: string;
  readonly "danger-soft": string;
  /** Keyboard focus ring (3:1 against every background it can sit on). */
  readonly focus: string;
  /** Categorical colours for widgets and charts, in a fixed order. */
  readonly "data-1": string;
  readonly "data-2": string;
  readonly "data-3": string;
  readonly "data-4": string;
  /** Overlay behind sheets and dialogs (alpha applied in CSS). */
  readonly scrim: string;
}

export type ColorToken = keyof ColorTheme;

/** Light theme: paper and chalkboard green (direction C, "Chalk & Prism", ADR-0041). */
export const light: ColorTheme = {
  bg: "#f4f1e8",
  surface: "#ffffff",
  "surface-2": "#ebe6d8",
  ink: "#1b2148",
  "ink-soft": "#4a5079",
  line: "#ddd7c7",
  "line-strong": "#87816f",
  accent: "#1e3f35",
  "accent-ink": "#ffffff",
  "accent-soft": "#dbe8e0",
  "accent-strong": "#173329",
  sun: "#f6c84c",
  good: "#237249",
  "good-soft": "#dff1e6",
  gentle: "#8a5a00",
  "gentle-soft": "#fbecd0",
  info: "#2f5d9a",
  "info-soft": "#e1eaf7",
  danger: "#b0322a",
  "danger-soft": "#f8e1de",
  focus: "#2457c5",
  "data-1": "#cf3a2e",
  "data-2": "#2f6fb5",
  "data-3": "#6a5ccc",
  "data-4": "#2e8a5c",
  scrim: "#1b2148",
};

/** Dark theme: Ardoise, a slate night (Pierre, 2026-10-27), never pure black (less glare for tired eyes). */
export const dark: ColorTheme = {
  bg: "#192026",
  surface: "#222a32",
  "surface-2": "#2b353e",
  ink: "#f1eee4",
  "ink-soft": "#bec7cf",
  line: "#36414b",
  "line-strong": "#808d99",
  accent: "#a5d9c2",
  "accent-ink": "#10221c",
  "accent-soft": "#24403b",
  "accent-strong": "#c4e9d8",
  sun: "#f6c84c",
  good: "#8fd4a8",
  "good-soft": "#1f3a2f",
  gentle: "#f0bd7d",
  "gentle-soft": "#3b3024",
  info: "#9cbdf0",
  "info-soft": "#22314a",
  danger: "#f2a29a",
  "danger-soft": "#45231f",
  focus: "#8fb4ff",
  "data-1": "#f08a7e",
  "data-2": "#8fc5ff",
  "data-3": "#b3aaf0",
  "data-4": "#8fd4a8",
  scrim: "#000000",
};

/**
 * "More contrast" (prefers-contrast: more, or the reading preference): darker text and
 * edges. Only the overridden colours are listed.
 */
export const lightMoreContrast: Partial<ColorTheme> = {
  "ink-soft": "#363b62",
  line: "#87816f",
  "line-strong": "#545040",
  accent: "#142d26",
  good: "#1b5a39",
  gentle: "#6b4500",
};

export const darkMoreContrast: Partial<ColorTheme> = {
  bg: "#11161b",
  "ink-soft": "#d6dce2",
  line: "#808d99",
  "line-strong": "#bec7cf",
  accent: "#c4e9d8",
  good: "#b3e6c5",
  gentle: "#f7d3a6",
};

/**
 * Pairs that must meet WCAG 2.2 AA in every theme: 4.5:1 for text (1.4.3), 3:1 for
 * large text, control edges, focus rings and meaningful graphics (1.4.11).
 */
export const contrastPairs: readonly {
  readonly fg: ColorToken;
  readonly bg: ColorToken;
  readonly min: 3 | 4.5;
  readonly why: string;
}[] = [
  ...(["bg", "surface", "surface-2"] as const).flatMap((bg) => [
    { fg: "ink" as const, bg, min: 4.5 as const, why: "body text" },
    { fg: "ink-soft" as const, bg, min: 4.5 as const, why: "secondary text" },
    { fg: "focus" as const, bg, min: 3 as const, why: "focus ring" },
  ]),
  { fg: "ink", bg: "accent-soft", min: 4.5, why: "learner bubble text" },
  { fg: "accent-strong", bg: "accent-soft", min: 4.5, why: "selected choice label" },
  { fg: "accent-ink", bg: "accent", min: 4.5, why: "primary button label" },
  { fg: "accent", bg: "surface", min: 4.5, why: "links and accent text" },
  { fg: "accent", bg: "bg", min: 4.5, why: "links on the page" },
  { fg: "line-strong", bg: "surface", min: 3, why: "input edges" },
  { fg: "line-strong", bg: "bg", min: 3, why: "control edges on the page" },
  { fg: "good", bg: "good-soft", min: 4.5, why: "right-answer feedback" },
  { fg: "good", bg: "surface", min: 4.5, why: "progress text" },
  { fg: "gentle", bg: "gentle-soft", min: 4.5, why: "not-yet feedback" },
  { fg: "gentle", bg: "surface", min: 4.5, why: "not-yet text" },
  { fg: "info", bg: "info-soft", min: 4.5, why: "tip text" },
  { fg: "info", bg: "surface", min: 4.5, why: "info text" },
  { fg: "danger", bg: "danger-soft", min: 4.5, why: "parent error text" },
  { fg: "danger", bg: "surface", min: 4.5, why: "parent error text" },
  { fg: "focus", bg: "accent-soft", min: 3, why: "focus ring on a selected choice" },
  { fg: "data-1", bg: "surface", min: 3, why: "chart and widget marks" },
  { fg: "data-2", bg: "surface", min: 3, why: "chart and widget marks" },
  { fg: "data-3", bg: "surface", min: 3, why: "chart and widget marks" },
  { fg: "data-4", bg: "surface", min: 3, why: "chart and widget marks" },
  { fg: "accent", bg: "surface-2", min: 3, why: "fraction shading in a well" },
];

/**
 * Type scale (rem, root 18px; 19px from 900px wide). Ratio 1.2 (minor third): calm
 * steps, big enough for 9-year-olds without shouting at 14-year-olds.
 */
export const typeScale = {
  xs: 0.78,
  sm: 0.89,
  md: 1,
  lg: 1.2,
  xl: 1.44,
  "2xl": 1.73,
  "3xl": 2.07,
} as const;

export const lineHeight = { tight: 1.2, body: 1.5, relaxed: 1.75 } as const;

export const fontWeight = { regular: 400, medium: 500, bold: 700 } as const;

export const fontStacks = {
  /** Default: Atkinson Hyperlegible, designed for low-vision readers (distinct I l 1, 0 O). */
  body: '"Atkinson Hyperlegible", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  /** Dyslexia-friendly option: Lexend widens letter shapes and spacing. */
  dyslexia: 'Lexend, "Atkinson Hyperlegible", system-ui, sans-serif',
  /** Strongest option for readers who ask for it by name. */
  opendyslexic: 'OpenDyslexic, Lexend, "Atkinson Hyperlegible", system-ui, sans-serif',
  /** Equation steps and code-like answers: tabular figures, clear operators. */
  math: '"Atkinson Hyperlegible", ui-monospace, "SF Mono", Menlo, Consolas, monospace',
  /** Headings, the wordmark and big numbers. Reading preferences replace it with theirs. */
  display: '"Space Grotesk", "Atkinson Hyperlegible", system-ui, sans-serif',
  /** Chalk handwriting for short labels only (a few words), never sentences or answers. */
  hand: 'Caveat, "Space Grotesk", cursive',
} as const;

/**
 * The prism: six decorative colours split from white light, used in a fixed order for
 * stripes, confetti and illustrations. Never text, never the only cue for meaning; the
 * same in both themes.
 */
export const prism = ["#cf3a2e", "#ff8a3d", "#f6c84c", "#3fae7a", "#8fc5ff", "#7b6fd6"] as const;

/** Spacing steps in px (4px grid). */
export const space = {
  "1": 4,
  "2": 8,
  "3": 12,
  "4": 16,
  "5": 24,
  "6": 32,
  "7": 48,
  "8": 64,
} as const;

/** Chalkboard corners: firm, not bubbly (direction C). Pills stay for chips and tracks. */
export const radius = { small: 10, medium: 16, large: 22, pill: 999 } as const;

/** Minimum touch target in px. WCAG 2.2 2.5.8 asks 24px; children need far more. */
export const tap = { min: 48, comfortable: 52, big: 64 } as const;

/** Motion: short and soft. Every duration becomes 0 under reduced motion. */
export const motion = {
  duration: { instant: 80, quick: 160, base: 260, slow: 420 },
  easing: {
    standard: "cubic-bezier(0.2, 0.7, 0.2, 1)",
    enter: "cubic-bezier(0, 0, 0.2, 1)",
    exit: "cubic-bezier(0.4, 0, 1, 1)",
    /** A small overshoot for celebrations only. */
    bounce: "cubic-bezier(0.34, 1.56, 0.64, 1)",
  },
} as const;

/** Layout widths in px. */
export const breakpoints = { narrow: 600, wide: 900, canvas: 1200 } as const;

export const shadow = {
  light: "0 1px 2px rgb(35 38 43 / 6%), 0 8px 24px rgb(35 38 43 / 8%)",
  dark: "0 1px 2px rgb(0 0 0 / 30%), 0 8px 24px rgb(0 0 0 / 30%)",
} as const;
